import { Tab } from '@ariakit/react/tab';
import cn from 'classnames';
import { useLayoutEffect, useMemo, useRef } from 'react';
import { useRect } from '#hooks/useRect.ts';
import { useUrlParams } from '#hooks/useUrlParams.ts';
import { useAnimatedTabs } from './context/useAnimatedTabs.tsx';
import TabStyles from './TabLink.module.css';

type Props = {
  /** Which query param should we look at? Defaults to `tab`. */
  queryParamKey?: string;
  /** Which value of the {@link queryParamKey} does this tab match on? */
  queryParamTarget: string;
} & React.HTMLAttributes<HTMLButtonElement>;

export function TabLink({ className, queryParamKey = 'tab', queryParamTarget, ...props }: Props) {
  const paramSchema = useMemo(() => ({ [queryParamKey]: 'scalar' as const }), [queryParamKey]);
  const [params] = useUrlParams(paramSchema);
  const isSelected = params[queryParamKey] === queryParamTarget;

  const { setActiveRect } = useAnimatedTabs();

  // measure the size of our element, only listen to rect if active
  const ref = useRef<HTMLButtonElement>(null);

  const rect = useRect(ref, { observe: isSelected });

  // callback to set styles whenever we're active
  useLayoutEffect(() => {
    if (isSelected) {
      setActiveRect(rect);
    }
  }, [isSelected, rect, setActiveRect]);

  return (
    <Tab
      id={queryParamTarget}
      className={cn(TabStyles.TabLink, { [TabStyles.TabLink___isActive]: isSelected }, className)}
      render={<button type="button" ref={ref} {...props} />}
    />
  );
}
